define('presentation/settings/billing/plans/facades/addPlanFacade',[
    'i18next',
    'common/promises/promiseFactory',
    'constants/billingPackageTypeConstants',
    'externalDependencies/clientWebSocket',
    'persistence/webSocket/webSocketApp'
], function () {

    const PromiseFactoryConstructor = require('common/promises/promiseFactory');

    const clientWebSocket = require('externalDependencies/clientWebSocket');
    const _accountPlanWebSocket = clientWebSocket.forApplication("accountPlan");

    const _billingPackageTypes = require('constants/billingPackageTypeConstants');

    let _promiseFactory = null;
    let _webSocketApp = null;

    const TELEPHONY_ONLY_PRICE_PER_MONTH  = 25;

    const _prepareTelephonyPackageForDisplay = ({accountPlan, availableBillingPackages}) => {
        const baseTelephonyPackage = availableBillingPackages.find(p => p.type === _billingPackageTypes.telephony.id);
        const telephonyPackage = baseTelephonyPackage;
        const currenNonTelephonyPackages = [];

        const liveReceptionistPackage = accountPlan.liveReceptionistPackage;
        const webChatPackage = accountPlan.webChatPackage;
        const outboundCallingPackage = accountPlan.outboundCallingPackage;

        if (liveReceptionistPackage) {
            currenNonTelephonyPackages.push(liveReceptionistPackage);
        }
        if (webChatPackage) {
            currenNonTelephonyPackages.push(webChatPackage);
        }
        if (outboundCallingPackage) {
            currenNonTelephonyPackages.push(outboundCallingPackage);
        }

        const addIncludedItems = (billingPackage) => {
            if (billingPackage) {
                if (billingPackage.includedChatSessions) {
                    telephonyPackage.includedChatSessions += billingPackage.includedChatSessions;
                }
                if (billingPackage.includedOutboundContacts) {
                    telephonyPackage.includedOutboundContacts += billingPackage.includedOutboundContacts;
                }
                if (billingPackage.includedMinutes) {
                    telephonyPackage.includedMinutes += billingPackage.includedMinutes;
                }
                if (billingPackage.includedScripts) {
                    telephonyPackage.includedScripts += billingPackage.includedScripts;
                }
            }
        };

        const setMeteredItems = (property) => {
            telephonyPackage[property] = currenNonTelephonyPackages.reduce((lowestPrice, billingPackage) => {
                if (billingPackage[property] !== null) {
                    return Math.min(lowestPrice, billingPackage[property]);
                }
                return lowestPrice;
            }, 0);
        };

        if (currenNonTelephonyPackages.length === 0) {
            telephonyPackage.pricePerMonth = TELEPHONY_ONLY_PRICE_PER_MONTH;
        } else {
            telephonyPackage.includedMinutes = 0;
        }

        addIncludedItems(liveReceptionistPackage);
        addIncludedItems(webChatPackage);
        addIncludedItems(outboundCallingPackage);

        setMeteredItems('pricePerAdditionalChatSession');
        setMeteredItems('pricePerAdditionalOutboundContact');
        setMeteredItems('pricePerAdditionalMinute');

        return telephonyPackage;
    };

    const _getAvailablePackages = (packageType) => {
        return _promiseFactory.deferIndefinitely((promise) => {
            let response = { accountPlan: null, availableBillingPackages: [] };
            let getAvailablePackagesPromiseFactory = new PromiseFactoryConstructor();

            if (packageType === _billingPackageTypes.telephony.id) {
                getAvailablePackagesPromiseFactory.defer((accountPlanPromise) => {
                    _getAccountPlan()
                        .fail(accountPlanPromise.reject)
                        .done((accountPlan) => {
                            response.accountPlan = accountPlan;
                            accountPlanPromise.resolve();
                        });
                });
            }

            getAvailablePackagesPromiseFactory.defer((billingPackagesPromise) => {
                _getBillingPackages(packageType)
                    .fail(billingPackagesPromise.reject)
                    .done((availableBillingPackages) => {
                        response.availableBillingPackages = availableBillingPackages;
                        billingPackagesPromise.resolve();
                    });
            });

            getAvailablePackagesPromiseFactory.wait()
                .fail(promise.reject)
                .done(() => {
                    if (packageType === _billingPackageTypes.telephony.id) {
                        promise.resolve([_prepareTelephonyPackageForDisplay(response)]);
                    } else {
                        promise.resolve(response.availableBillingPackages);
                    }
                });
        });
    };

    const _getAccountPlan = () => _promiseFactory.defer((promise) => {
        _accountPlanWebSocket.send("getAccountPlan", {}, (response) => {
            promise.resolve(response);
        });
    });

    const _getCurrentPackage = (packageType) => {
        return _promiseFactory.defer((promise) => {
            const request = {
                type: packageType
            };
            _accountPlanWebSocket.send("getAccountPackage", request, (response) => {
                promise.resolve(response);
            });
        });
    };

    const _getBillingPackages = (packageType) => {
        return _promiseFactory.deferIndefinitely((promise) => {
            const request = {
                type: packageType || null
            };
            _accountPlanWebSocket.send("getBillingPackages", request, (response) => {
                promise.resolve(response);
            });
        });
    };

    const _updatePackage = (packageType, packageId, isOnboardingComplete) => {
        return _promiseFactory.deferIndefinitely((promise) => {
            const request = {
                packageType,
                packageId,
                isOnboardingComplete,
            };
            _webSocketApp.send("update_package", request, (response) => {
                promise.resolve(response);
            });
        });
    };

    const _init = (promiseFactory) => {
        const WebSocketAppConstructor = require('persistence/webSocket/webSocketApp');
        _webSocketApp = new WebSocketAppConstructor();
        _webSocketApp.init("account_plan");

        _promiseFactory = promiseFactory;
    };

    return function () {
        let self = this;

        self.init = _init;
        self.getAccountPlan = _getAccountPlan;
        self.getAvailablePackages = _getAvailablePackages;
        self.getCurrentPackage = _getCurrentPackage;
        self.getBillingPackages = _getBillingPackages;
        self.updatePackage = _updatePackage;
    };
});
